import React, { Fragment } from 'react';
import {
	ModalContainer,
	Paragraph,
	Spinner,
	TextArea,
	tokens,
} from '@welbe/web-design-system';
import { Button } from '@welbe/design-system';
import {
	Container,
	Subtitle,
	TableContainer,
	ButtonContainer,
	TdName,
	TdType,
	TdFrequency,
	ExposureContainer,
	Banner,
	Title,
	InfoContainer,
	Benefits,
	BenefitItem,
	MoreInfo,
	FileContainer,
} from './ModalFactor.styles';
import {
	SummaryJobRisksExams,
	SummaryJobRiskFactors,
	SymmaryJobRisks,
	SummaryJobProfileType,
} from 'Views/JobProfile/pages/SummaryRisk/SummaryRisk.types';
import { FiEdit } from 'react-icons/fi';
import {
	ExpositionFrequencyEnum,
	ExpositionLevelEnum,
} from 'Views/JobProfile/pages/FormRisk/components/ExpositionLevel/ExpositionLevel.const';
import {
	examTypes,
	frequencyTypes,
} from 'Views/JobProfile/pages/FormRisk/components/RecommendedExams/RecommendedExams.const';
import {
	benefitItems,
	examResultOptions,
} from 'Views/JobProfile/pages/FormRisk/components/Result/Result';
import { useModalFactor } from './ModalFactor.hooks';
import calendar from '../../../../../../App/assets/img/checklist-icon-big.png';
import { JobProfileType } from 'Views/JobProfile/JobProfile.enums';
import { ReactComponent as File } from 'App/assets/img/file-text.svg';

type ModalFactorProps = {
	onEdit: (riskId: string) => void;
	onClose: () => void;
	summaryJobProfile: SummaryJobProfileType;
	selectedRisk: SymmaryJobRisks;
	selectedFactor: SummaryJobRiskFactors;
};

export const ModalFactor = ({
	onClose,
	onEdit,
	summaryJobProfile,
	selectedRisk,
	selectedFactor,
}: ModalFactorProps) => {
	const { isCompanyPermited, loadingFile, handleOpenChat, handleOpenFile } =
		useModalFactor();

	const getTypeLabel = (type: string) => {
		return (
			examTypes.find((examType) => {
				if (examType.id === (type as string)) return examType.value;
			})?.value || ''
		);
	};

	const getFrequencyLabel = (frequency: string | string[]) => {
		return (
			frequencyTypes.find((frequencyType) => {
				if (frequencyType.id === (frequency as string))
					return frequencyType.value;
			})?.value || ''
		);
	};

	const getExpositionLevelLabel = () => {
		return (
			ExpositionLevelEnum.find((level) => {
				if (level.id === selectedFactor.exposureLevel) return level.value;
			})?.value || '-'
		);
	};

	const getExpositionFrequencyLabel = () => {
		return (
			ExpositionFrequencyEnum.find((exposicion) => {
				if (exposicion.id === selectedFactor.exposureFrequency)
					return exposicion.value;
			})?.value || '-'
		);
	};

	const getExposureLabel = () => {
		return (
			<ExposureContainer>
				{selectedFactor.exposure?.map((exposure, index) => (
					<li key={index}>{exposure}</li>
				))}
			</ExposureContainer>
		);
	};

	const getExamLabel = () => {
		return examResultOptions.find((option) => {
			if (option.id === selectedFactor.examResult) return option.value;
		})?.value;
	};

	return (
		<ModalContainer visible width="800px" height="auto" closeOnlyOnTheButton>
			<Container>
				<Paragraph
					fontWeight="600"
					fontSize="16px"
					textAlign="center"
					padding="0px 20px 30px 20px"
					color={tokens.color_primary}
				>{`Riesgo para ${selectedRisk.name} - ${selectedFactor.name}`}</Paragraph>
				{(selectedFactor.exposureLevel ||
					selectedFactor.exposureFrequency ||
					!!selectedFactor.exposure ||
					selectedFactor.examResult) && (
					<Subtitle>
						{selectedFactor.exposureLevel && (
							<div>
								<Paragraph fontWeight="600" color={tokens.color_primary}>
									Nivel de exposición
								</Paragraph>
								<Paragraph>{getExpositionLevelLabel()}</Paragraph>
							</div>
						)}
						{selectedFactor.exposureFrequency && (
							<div>
								<Paragraph fontWeight="600" color={tokens.color_primary}>
									Frecuencia de exposición
								</Paragraph>
								<Paragraph>{getExpositionFrequencyLabel()}</Paragraph>
							</div>
						)}
						{!!selectedFactor.exposure?.length && (
							<div>
								<Paragraph
									fontWeight="600"
									padding="0 0 5px 0"
									color={tokens.color_primary}
								>
									Sustancia o agente biológico
								</Paragraph>
								<Paragraph>{getExposureLabel()}</Paragraph>
							</div>
						)}
						{selectedFactor.examResult && (
							<div>
								<Paragraph
									fontWeight="600"
									padding="0 0 5px 0"
									color={tokens.color_primary}
								>
									Resultado del estudio
								</Paragraph>
								<Paragraph>{getExamLabel()}</Paragraph>
							</div>
						)}
						{summaryJobProfile.type === JobProfileType.EXPOSITION_AREA &&
							selectedRisk.optionType?.includes('expositionLevel') && (
								<FileContainer hasFile={!!selectedFactor.examFileName}>
									{loadingFile ? (
										<div>
											<Spinner />
										</div>
									) : (
										<File
											width={'20px'}
											onClick={() =>
												selectedFactor.examFileName &&
												handleOpenFile(selectedFactor)
											}
										/>
									)}
									<Paragraph
										cursor={selectedFactor.examFileName ? 'pointer' : 'default'}
										fontWeight="500"
										color={tokens.color_primary}
										onClick={() =>
											selectedFactor.examFileName
												? handleOpenFile(selectedFactor)
												: undefined
										}
									>
										{selectedFactor.examFileName || 'Sin archivo'}
									</Paragraph>
								</FileContainer>
							)}
					</Subtitle>
				)}

				{!isCompanyPermited && selectedRisk.optionType?.includes('result') && (
					<Banner>
						<Title>
							<img src={calendar} alt="" width="55px" />
							<div>
								<Paragraph
									fontSize="16px"
									fontWeight="600"
									color={tokens.color_primary}
								>
									Campañas NOM - 035
								</Paragraph>
								<Paragraph fontWeight="600">
									¿Quieres evaluar factores de riesgo psicosocial en tu empresa?
								</Paragraph>
							</div>
						</Title>

						<hr className="horizontal-ruller" />

						<InfoContainer>
							<Paragraph
								fontWeight="600"
								fontSize="16px"
								margin="0 0 30px 0"
								color={tokens.color_primary}
							>
								Mejora tu plan para acceder a todos los beneficios
							</Paragraph>
							<Benefits>
								{benefitItems.map((benefit) => (
									<Fragment key={benefit.text}>
										<BenefitItem>
											<img src={benefit.image} alt="" width="55px" />
											<Paragraph fontWeight="500" fontSize="14px">
												{benefit.text}
											</Paragraph>
										</BenefitItem>
									</Fragment>
								))}
							</Benefits>
						</InfoContainer>
						<MoreInfo>
							<Button width="240px" onClick={handleOpenChat}>
								Más información
							</Button>
						</MoreInfo>
					</Banner>
				)}

				{!!selectedFactor?.exams?.length && (
					<TableContainer>
						<tr>
							<th>Exámenes aplicados</th>
							<th>Periodicidad</th>
							<th>Frecuencia</th>
						</tr>
						<>
							{selectedFactor?.exams?.map((exam: SummaryJobRisksExams) => (
								<tr key={exam.id}>
									<TdName>
										<Paragraph fontWeight="500" padding="0 0 0 10px">
											{exam.name}
										</Paragraph>
									</TdName>
									<TdType>
										{exam?.type?.map((item) => (
											<>
												{item && (
													<li key={item} title={getTypeLabel(item)}>
														{getTypeLabel(item)}
													</li>
												)}
											</>
										))}
									</TdType>
									<TdFrequency title={getFrequencyLabel(exam.frequency)}>
										{getFrequencyLabel(exam.frequency)}
									</TdFrequency>
								</tr>
							))}
						</>
					</TableContainer>
				)}
				{selectedFactor.actualConditions && (
					<div>
						<Paragraph
							fontSize="14px"
							fontWeight="400"
							color={tokens.color_primary}
							padding="20px 0 0 0"
						>
							Condiciones de riesgo actuales
						</Paragraph>
						<TextArea
							id="actualConditions"
							disabled
							value={selectedFactor.actualConditions}
						/>
					</div>
				)}
				{selectedFactor.recommendedActions && (
					<div>
						<Paragraph
							fontSize="14px"
							fontWeight="400"
							padding="25px 0 0 0"
							color={tokens.color_primary}
						>
							Acciones y/o recomendaciones
						</Paragraph>
						<TextArea
							id="recommendedActions"
							value={selectedFactor.recommendedActions}
							disabled
						/>
					</div>
				)}
				<ButtonContainer>
					<Button variant="outline" onClick={onClose} padding="5px">
						Regresar
					</Button>
					<Button onClick={() => onEdit(selectedRisk.id)} padding="5px">
						Editar <FiEdit size={24} />
					</Button>
				</ButtonContainer>
			</Container>
		</ModalContainer>
	);
};
